import React from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';

import { IconButton } from '../../atoms';
import { X as XIcon } from '../../icons';

const useStyles = makeStyles((theme: Theme) => ({
  childrenContainer: {
    background: theme.palette.grey[200],
    margin: theme.spacing(0, -2, 2),
  },
  childrenContent: {
    marginTop: -theme.spacing(2.5),
    padding: theme.spacing(0, 3, 1.5, 2),
  },
  childrenContentNoCloseBtn: {
    marginTop: 0,
    padding: theme.spacing(1.5, 2, 1.5),
  },
  closeButton: {
    background: theme.palette.grey[200],
    display: 'flex',
    margin: '0px 0px auto auto',
    padding: theme.spacing(1),

    '&.MuiIconButton-colorPrimary:hover': {
      backgroundColor: 'transparent',
      '& svg': {
        color: theme.palette.common.black,
      },
    },
  },
  pointer: {
    borderColor: `transparent transparent ${theme.palette.grey[200]} transparent`,
    borderStyle: 'solid',
    borderWidth: '16px 16px 16px 16px',
    height: '0px',
    width: '0px',
  },
  pointerRight: {
    margin: '-22px 0px auto auto', // -22px = -32px pointer container height +10px for margin & aligning right
    marginRight: theme.spacing(0.25),
  },
  pointerLeft: {
    margin: '-22px auto auto 0px', // -22px = -32px pointer container height +10px for margin & aligning left
    marginLeft: theme.spacing(0.25),
  },
  pointerCenter: {
    margin: '-22px auto auto auto', // -22px = -32px pointer container height +10px for margin & aligning center
  },

  [theme.breakpoints.up('sm')]: {
    childrenContainer: {
      margin: theme.spacing(0, 0, 2),
    },
    pointerRight: {
      marginRight: theme.spacing(4),
    },
    pointerLeft: {
      marginLeft: theme.spacing(4),
    },
  },
}));

export type PopoutProps = {
  children: JSX.Element;
  className?: string;
  onClosePopout?: () => void;
  pointerVariant?: 'left' | 'center' | 'right' | 'none';
  popoutName?: string;
  showCloseButton?: boolean;
  showPopout: boolean;
};

export function Popout(props: PopoutProps): JSX.Element {
  const {
    children,
    className = '',
    onClosePopout = (): void => {},
    pointerVariant = 'right',
    popoutName = '',
    showCloseButton = true,
    showPopout,
  } = props;
  const classes = useStyles();

  const popoutStyles = clsx(
    'fd-popout',
    popoutName && `fd-popout__${popoutName.toLowerCase()}`,
    className !== '' && className,
  );
  const pointerStyles = clsx(
    `fd-popout__pointer_${pointerVariant}`,
    classes.pointer,
    pointerVariant === 'right' && classes.pointerRight,
    pointerVariant === 'left' && classes.pointerLeft,
    pointerVariant === 'center' && classes.pointerCenter,
  );
  const childrenStyles = clsx(
    'fd-popout__children',
    classes.childrenContent,
    !showCloseButton && classes.childrenContentNoCloseBtn,
  );

  if (!showPopout) {
    return null;
  }

  return (
    <div className={popoutStyles}>
      {pointerVariant !== 'none' && <div className={pointerStyles} />}

      <div className={classes.childrenContainer}>
        {showCloseButton && (
          <IconButton
            className={classes.closeButton}
            disableRipple
            onClick={onClosePopout}
          >
            <XIcon size="xs" />
          </IconButton>
        )}

        <div className={childrenStyles}>{children}</div>
      </div>
    </div>
  );
}
